import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Navigation, PackageOpen, PackageCheck, Phone, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/Brand";
import { Timeline } from "@/components/Timeline";
import { IssuesList } from "@/components/IssuesList";
import { DocUploader } from "@/components/driver/DocUploader";
import { IssueDialog } from "@/components/driver/IssueDialog";
import { DRIVER_STEPS, STATUS_COLORS, STATUS_FLOW, getPosition, mapsUrl, nextStep } from "@/lib/helpers";
import { t } from "@/lib/i18n";

function StopCard({ stop, index, lang }) {
  const isLoad = stop.type === "loading";
  const Icon = isLoad ? PackageOpen : PackageCheck;
  const phone = stop.contact?.match(/\+?\d[\d\s/-]{6,}/)?.[0];
  return (
    <div className={`rounded-xl border p-4 ${isLoad ? "border-violet-500/30 bg-violet-500/[0.06]" : "border-cyan-500/30 bg-cyan-500/[0.06]"}`} data-testid={`driver-stop-${index}`}>
      <div className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] ${isLoad ? "text-violet-300" : "text-cyan-300"}`}>
        <Icon className="h-4 w-4" /> {index + 1}. {t(lang, stop.type)}
      </div>
      {(stop.date || stop.time) && <div className="mt-2 font-display text-xl font-bold">{stop.date} {stop.time}</div>}
      {stop.company && <div className="mt-1 font-semibold">{stop.company}</div>}
      <div className="mt-1 text-sm text-slate-300">{stop.address}</div>
      {stop.reference && <div className="mt-2 text-sm"><span className="text-slate-400">{t(lang, "reference")}:</span> <span className="font-mono">{stop.reference}</span></div>}
      {stop.contact && <div className="mt-1 text-sm"><span className="text-slate-400">{t(lang, "contact")}:</span> {stop.contact}</div>}
      {stop.notes && <div className="mt-2 rounded-lg bg-black/30 p-2.5 text-sm text-slate-200">{stop.notes}</div>}
      <div className="mt-4 flex gap-2">
        {stop.address && (
          <a href={mapsUrl(stop.address)} target="_blank" rel="noreferrer" data-testid={`navigate-stop-${index}`}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 font-semibold text-white active:bg-blue-700">
            <Navigation className="h-4 w-4" /> {t(lang, "navigate")}
          </a>
        )}
        {phone && (
          <a href={`tel:${phone.replace(/[\s/-]/g, "")}`} data-testid={`call-stop-${index}`} className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/15">
            <Phone className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}

function Progress({ status }) {
  const idx = STATUS_FLOW.indexOf(status);
  return (
    <div className="flex gap-1.5" data-testid="driver-progress">
      {DRIVER_STEPS.map((s) => (
        <div key={s} className="h-1.5 flex-1 rounded-full transition-colors" style={{ background: STATUS_FLOW.indexOf(s) <= idx ? STATUS_COLORS[s] : "#1E293B" }} />
      ))}
    </div>
  );
}

function NextStepButton({ order, lang, onDone }) {
  const [busy, setBusy] = useState(false);
  const step = nextStep(order.status);
  if (!step) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-300" data-testid="driver-all-done">
        <CheckCircle2 className="h-6 w-6" /> <span className="font-semibold">{t(lang, "all_done")}</span>
      </div>
    );
  }
  const go = async () => {
    setBusy(true);
    const pos = await getPosition();
    try {
      await api.post(`/driver/orders/${order.id}/status`, { status: step, lat: pos?.lat ?? null, lng: pos?.lng ?? null });
      toast.success(t(lang, pos ? "location_saved" : "location_off"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e, t(lang, "error")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="eyebrow mb-2">{t(lang, "next_step")}</div>
      <button onClick={go} disabled={busy} data-testid={`status-${step}-btn`}
        className="flex min-h-[64px] w-full items-center justify-center gap-3 rounded-2xl font-display text-2xl font-bold uppercase text-white shadow-lg transition-transform active:scale-[0.98] disabled:opacity-70"
        style={{ background: STATUS_COLORS[step], boxShadow: `0 10px 30px -10px ${STATUS_COLORS[step]}` }}>
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <CheckCircle2 className="h-6 w-6" />} {t(lang, `step_${step}`)}
      </button>
    </div>
  );
}

export default function DriverOrder() {
  const { id } = useParams();
  const { lang } = useOutletContext();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  const load = useCallback(() => api.get(`/driver/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setMissing(true)), [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (missing) return <p className="py-10 text-center text-slate-500" data-testid="driver-order-missing">{t(lang, "no_orders")}</p>;
  if (!order) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;

  const cargo = [["cargo", order.cargo], ["weight", order.weight], ["ldm", order.loading_meters], ["pallets", order.pallets], ["vehicle", order.vehicle]].filter(([, v]) => v);

  return (
    <div className="space-y-5 pb-6">
      <Link to="/driver" data-testid="driver-back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-400"><ArrowLeft className="h-4 w-4" /> {t(lang, "back")}</Link>
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs text-slate-400" data-testid="driver-order-ref">{order.reference}</span>
          <StatusBadge status={order.status} lang={lang} testId="driver-order-status" />
        </div>
        <div className="mt-3"><Progress status={order.status} /></div>
      </div>

      <NextStepButton order={order} lang={lang} onDone={load} />

      <div className="space-y-3">{order.stops.map((s, i) => <StopCard key={i} stop={s} index={i} lang={lang} />)}</div>

      {(cargo.length > 0 || order.driver_notes) && (
        <div className="panel space-y-2" data-testid="driver-cargo">
          {cargo.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 text-sm"><span className="text-slate-400">{t(lang, k)}</span><span className="text-right font-medium">{v}</span></div>
          ))}
          {order.driver_notes && <div className="mt-2 rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-sm"><div className="eyebrow mb-1 text-blue-300">{t(lang, "office_notes")}</div>{order.driver_notes}</div>}
        </div>
      )}

      <DocUploader order={order} lang={lang} onUploaded={load} />

      <button onClick={() => setIssueOpen(true)} data-testid="report-issue-btn" className="flex h-14 w-full items-center justify-center gap-2 rounded-xl border border-red-500/50 bg-red-500/10 font-semibold text-red-300 active:bg-red-500/20">
        <AlertTriangle className="h-5 w-5" /> {t(lang, "report_issue")}
      </button>
      {order.issues.length > 0 && (
        <div className="space-y-2"><div className="eyebrow">{t(lang, "issues")}</div><IssuesList issues={order.issues} files={order.files} lang={lang} /></div>
      )}

      <div className="panel"><div className="eyebrow mb-4">{t(lang, "history")}</div><Timeline history={order.status_history} lang={lang} /></div>
      {issueOpen && <IssueDialog orderId={order.id} lang={lang} onClose={() => setIssueOpen(false)} onSent={() => { setIssueOpen(false); load(); }} />}
    </div>
  );
}
